import React, { useCallback, useMemo, useState } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import moment from "moment";
import { useSelector } from "react-redux";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import {
  HEADER_ACTION_BUTTON_WIDTH,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  leavePalette,
  cardSx,
  canonicalStatus,
  appliedOn,
  formatBreakDuration,
  ATTENDANCE_TYPES,
} from "./attendanceHelpers";
import { AttendanceStatusTiles } from "./AttendanceStatusTiles";
import { AttendanceHistoryTable } from "./AttendanceHistoryTable";
import { stripEmployeeIdFromName } from "pages/TeamZone/Approve/displayNameUtils";

// Typed history-table columns per attendance type. Permission and On Duty share
// a date + time window; Comp Off uses the earned/requested dates it stores.
// Types feed both the shared CustomFilter (search / column filters / sort) and
// the SavedViewSelector "Default View" dropdown, exactly like My Attendance.
const HEADERS = {
  permission: [
    { name: "Date", key: "date", type: "date", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
    { name: "From - To", key: "window", type: "string", icon: <AccessTimeOutlinedIcon fontSize="small" /> },
    { name: "Duration", key: "duration", type: "string", icon: <HourglassBottomOutlinedIcon fontSize="small" /> },
    { name: "Reason", key: "reason", type: "string", icon: <SubjectOutlinedIcon fontSize="small" /> },
    { name: "Status", key: "status", type: "autocomplete", icon: <CheckCircleOutlineIcon fontSize="small" /> },
    { name: "Applied On", key: "applied", type: "date", icon: <EventAvailableOutlinedIcon fontSize="small" /> },
  ],
  onduty: [
    { name: "On Duty", key: "type_label", type: "autocomplete", icon: <CategoryOutlinedIcon fontSize="small" /> },
    { name: "Date", key: "date", type: "date", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
    { name: "From - To", key: "window", type: "string", icon: <AccessTimeOutlinedIcon fontSize="small" /> },
    { name: "Duration", key: "duration", type: "string", icon: <HourglassBottomOutlinedIcon fontSize="small" /> },
    { name: "Reason", key: "reason", type: "string", icon: <SubjectOutlinedIcon fontSize="small" /> },
    { name: "Status", key: "status", type: "autocomplete", icon: <CheckCircleOutlineIcon fontSize="small" /> },
    { name: "Applied On", key: "applied", type: "date", icon: <EventAvailableOutlinedIcon fontSize="small" /> },
  ],
  compoff: [
    { name: "Comp Off", key: "type_label", type: "autocomplete", icon: <CategoryOutlinedIcon fontSize="small" /> },
    { name: "Earned On", key: "earned_date", type: "date", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
    { name: "Requested For", key: "requested_date", type: "date", icon: <EventAvailableOutlinedIcon fontSize="small" /> },
    { name: "Reason", key: "reason", type: "string", icon: <SubjectOutlinedIcon fontSize="small" /> },
    { name: "Status", key: "status", type: "autocomplete", icon: <CheckCircleOutlineIcon fontSize="small" /> },
    { name: "Applied On", key: "applied", type: "date", icon: <EventAvailableOutlinedIcon fontSize="small" /> },
  ],
  break: [
    { name: "Apply Break", key: "type_label", type: "autocomplete", icon: <CategoryOutlinedIcon fontSize="small" /> },
    { name: "Date", key: "date", type: "date", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
    { name: "Duration", key: "duration", type: "string", icon: <HourglassBottomOutlinedIcon fontSize="small" /> },
    { name: "Reason", key: "reason", type: "string", icon: <SubjectOutlinedIcon fontSize="small" /> },
    { name: "Status", key: "status", type: "autocomplete", icon: <CheckCircleOutlineIcon fontSize="small" /> },
    { name: "Applied On", key: "applied", type: "date", icon: <EventAvailableOutlinedIcon fontSize="small" /> },
  ],
};

const cleanUserNameForReport = (name, employeeId) => {
  const text = (name || "").toString().replace(/\s+/g, " ").trim();
  const id = (employeeId || "").toString().trim();
  if (!text || !id) return text || "N/A";
  return text
    .replace(new RegExp(`\\s*[-–—|/]?\\s*${id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`, "i"), "")
    .trim() || text;
};

// Was a locally re-implemented copy of displayNameUtils' name-cleaning idea,
// but its fallback (no explicit id passed) only stripped a trailing run of
// digits — it left a letter-prefixed employee code (e.g. "EMP045") stuck
// straight onto the name with no space. approved_by/rejected_by/cancelled_by/
// approver_name below never had a per-action id to pass in, so they always
// hit that weak fallback.
const cleanAdjustmentDisplayName = stripEmployeeIdFromName;

const toDateIso = (value) => {
  const parsed = moment(
    value,
    ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", "DD MMM YYYY", moment.ISO_8601],
    true,
  );
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

// One Leave-style card screen for a single attendance type. It is fully derived
// from the shared records array it is handed (filtered by the type's matcher) and
// defers creating/viewing to the drawers the parent owns via onNew / onRowClick.
export const AttendanceRequestScreen = ({
  typeKey,
  data = [],
  checkAccess,
  subModuleId,
  onNew,
  onRowClick,
}) => {
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const meta = ATTENDANCE_TYPES[typeKey];
  const fullHeaders = HEADERS[typeKey] || [];

  const [statusFilter, setStatusFilter] = useState("");

  // CustomFilter output + pagination.
  const [tableRows, setTableRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  // Default range is This Month (see getThisMonthRange).
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "monthEnd" }),
  );
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);

  const typeRows = useMemo(
    () => (Array.isArray(data) ? data.filter((r) => meta.matches(r)) : []),
    [data, meta],
  );

  const dateFilteredTypeRows = useMemo(
    () =>
      typeRows.filter((row) => {
        const rowDate =
          toDateIso(row?.date) ||
          toDateIso(row?.start_date) ||
          toDateIso(row?.earned_date) ||
          toDateIso(row?.requested_date);
        if (!rowDate) return true;
        return rowDate >= startDate && rowDate <= endDate;
      }),
    [endDate, startDate, typeRows],
  );

  const counts = useMemo(
    () =>
      dateFilteredTypeRows.reduce((acc, r) => {
        const s = canonicalStatus(r.status);
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      }, {}),
    [dateFilteredTypeRows],
  );

  // Rows fed to CustomFilter: pre-filtered by the clickable status tiles, then
  // normalized so every history column addresses a flat row[key] the shared
  // filter/search/sort can read directly.
  const filterInputRows = useMemo(() => {
    return dateFilteredTypeRows
      .filter((r) => !statusFilter || canonicalStatus(r.status) === statusFilter)
      .map((r) => ({
        ...r,
        employee_name: cleanAdjustmentDisplayName(r.employee_name, r.employee_id),
        applied_by: cleanAdjustmentDisplayName(r.applied_by, r.employee_id),
        approved_by: cleanAdjustmentDisplayName(r.approved_by),
        rejected_by: cleanAdjustmentDisplayName(r.rejected_by),
        cancelled_by: cleanAdjustmentDisplayName(r.cancelled_by),
        approver_name: cleanAdjustmentDisplayName(r.approver_name),
        type_label: meta.typeLabel ? meta.typeLabel(r) : meta.label,
        window:
          r.start_time || r.end_time
            ? `${r.start_time || "—"} - ${r.end_time || "—"}`
            : "—",
        reason: r.reason || r.applied_reason || r.description || "",
        applied: appliedOn(r),
        duration:
          typeKey === "break"
            ? formatBreakDuration(r.duration)
            : r.duration,
      }));
  }, [dateFilteredTypeRows, statusFilter, meta, typeKey]);

  const handleFilteredDataChange = useCallback(
    (filtered = []) => {
      setTableRows(filtered);
      setPage((prev) => (prev * rowsPerPage >= filtered.length ? 0 : prev));
    },
    [rowsPerPage],
  );

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDateRangeChange = useCallback((rangeStart, rangeEnd, rangeLabel) => {
    setStartDate(moment(rangeStart).format("YYYY-MM-DD"));
    setEndDate(moment(rangeEnd).format("YYYY-MM-DD"));
    setDateRangeLabel(rangeLabel || "Custom Range");
    setPage(0);
  }, []);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: `myzone_attendance_${typeKey}_saved_views`,
    fullHeaders,
    screenAccess: true,
    onViewChange: () => setPage(0),
  });

  const reportMetadata = useMemo(
    () => ({
      "User Name": cleanUserNameForReport(
        userDetails?.username || userDetails?.user_name,
        userDetails?.employee_id,
      ),
      "Employee ID": userDetails?.employee_id || "N/A",
      Type: meta.label,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [meta.label, userDetails],
  );

  const paginatedRows = useMemo(
    () => tableRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [tableRows, page, rowsPerPage],
  );

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: leavePalette.text }}>
            {meta.title}
          </Typography>
          <Typography variant="body2" sx={{ color: leavePalette.subText }}>
            {meta.subtitle}
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.25}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <ApprovalDateRangeFilter
            startDate={startDate}
            endDate={endDate}
            dateRangeLabel={dateRangeLabel}
            onChange={handleDateRangeChange}
            includeCurrentDate
            showToday
          />
          {checkAccess ? (
            <PrimaryButton
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              sx={{
                minWidth: { xs: 0, sm: HEADER_ACTION_BUTTON_WIDTH },
                width: { xs: "100%", sm: "auto" },
              }}
              onClick={onNew}
            >
              {meta.newLabel}
            </PrimaryButton>
          ) : null}
        </Stack>
      </Stack>

      <Grid container spacing={2.5}>
        {/* Main column */}
        <Grid item xs={12}>
          <Box sx={{ ...cardSx, p: 2.5 }}>
            <AttendanceStatusTiles
              counts={counts}
              activeStatus={statusFilter}
              onSelect={(s) => {
                setStatusFilter(s);
                setPage(0);
              }}
            />

            {/* My Attendance-style toolbar: Default View · Search · Download · Sort · Filters */}
            <CustomFilter
              title={`${meta.label} History`}
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={filterInputRows}
              filteredData={tableRows}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={true}
              showSaveButton={true}
              hideCount={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={reportMetadata}
              labeledToolbar={true}
              searchPlaceholder="Search by reason, date or status..."
            />

            <AttendanceHistoryTable
              title={`${meta.label} History`}
              headers={visibleHeaders}
              rows={paginatedRows}
              meta={meta}
              onRowClick={onRowClick}
            />

            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${leavePalette.border}` }}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: tableRows.length,
                itemLabel: "records",
                onPageChange: setPage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions,
              })}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
